import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAlert } from './ConfirmModal';
import API from '../utils/api';
import usePricing from '../utils/usePricing';
import { openCheckoutUrl } from '../utils/twa';
import { shouldUseIap, purchaseProduct, TOPUP_PRODUCT_ID, getIapPriceMap, paymentStoreName } from '../utils/iap';
import useLimits from '../utils/useLimits';
import { getAffiliateCookieCode } from '../utils/affiliate';

// packType: 'stories' | 'narrations' | 'cloneNarrations' | null (shows all)
const TopUpModal = ({ show, onClose, packType = null, planName = 'basic' }) => {
  const alert = useAlert();
  const { pricing } = usePricing();
  const currency = pricing?.currency || 'USD';
  const { refresh: refreshLimits } = useLimits();

  const [packs, setPacks]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying]   = useState(null); // packId being purchased
  const [iapPrices, setIapPrices] = useState({}); // productId → localized store price (native)

  useEffect(() => {
    if (!show) return;
    API.get(`/topup/packs?currency=${currency}`)
      .then(res => setPacks(res.data.packs || []))
      .catch(() => {});
    // On native, load the real App Store / Play prices (already in local currency).
    if (shouldUseIap()) {
      getIapPriceMap(Object.values(TOPUP_PRODUCT_ID)).then(setIapPrices).catch(() => {});
    }
  }, [show, currency]);

  // Price to show: the store's localized price on native, else the Stripe price.
  const packPrice = (pack) => iapPrices[TOPUP_PRODUCT_ID[pack.id]] || pack.priceDisplay;

  if (!show) return null;

  // Filter to the relevant pack if a specific limit was hit
  const typeMap = { stories: 'story', narrations: 'narration', cloneNarrations: 'voice', regens: 'regen', photoIllustrations: 'photo' };
  const filteredPacks = packType
    ? packs.filter(p => p.id === typeMap[packType])
    : packs;

  const handleBuy = async (packId) => {
    // iOS: buy the consumable via Apple IAP; the RevenueCat webhook adds credits.
    if (shouldUseIap()) {
      setBuying(packId);
      try {
        const r = await purchaseProduct(TOPUP_PRODUCT_ID[packId] || `topup_${packId}`);
        if (r.cancelled) { setBuying(null); return; }
        // Credits are granted server-side by the webhook — poll limits briefly.
        for (let i = 0; i < 5; i++) { await new Promise(res => setTimeout(res, 1500)); await refreshLimits?.(); }
        await alert({ title: 'Purchase complete 🎉', message: 'Your credits have been added to your account.' });
        onClose?.();
      } catch (err) {
        await alert({ title: 'Purchase failed', message: err.message || 'Could not complete the purchase.', danger: true });
      } finally { setBuying(null); }
      return;
    }
    setBuying(packId);
    try {
      const affiliateCode = getAffiliateCookieCode() || '';
      const res = await API.post('/topup/checkout', { packId, currency, ...(affiliateCode && { affiliateCode }) });
      openCheckoutUrl(res.data.url);
    } catch (err) {
      await alert({ title: 'Checkout failed', message: err.response?.data?.message || 'Could not start checkout. Please try again.', danger: true });
      setBuying(null);
    }
  };

  const PACK_STYLES = {
    story:     { accent: '#6f42c1', bg: 'var(--purple-section)',  border: 'var(--border-purple2)' },
    narration: { accent: '#0d6efd', bg: 'var(--blue-section)',   border: 'var(--border-blue)' },
    voice:     { accent: '#ea580c', bg: 'var(--orange-section)', border: 'var(--border-orange)' },
    regen:     { accent: '#059669', bg: 'var(--green-section)',  border: 'var(--border-green)' },
    photo:     { accent: '#db2777', bg: 'var(--purple-section-2)', border: 'var(--border-purple2)' },
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 1040, backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        zIndex: 1050, width: '100%', maxWidth: '520px', padding: '0 16px',
      }}>
        <div className="card border-0 shadow-lg" style={{ borderRadius: '24px', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '28px 28px 20px' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>⚡</div>
                <h5 className="fw-bold text-white mb-1" style={{ fontFamily: "'Fredoka One',cursive", fontSize: '22px' }}>
                  Top-up your credits
                </h5>
                <p style={{ color: '#c9a8ff', fontSize: '13px', margin: 0 }}>
                  Buy a one-time pack — credits never expire and stack with your plan.
                </p>
              </div>
              <button
                onClick={onClose}
                className="btn-close btn-close-white ms-3"
                style={{ flexShrink: 0 }}
              />
            </div>
          </div>

          {/* Pack cards */}
          <div style={{ padding: '20px 24px 24px', background: 'var(--card-bg)' }}>
            {filteredPacks.length === 0 ? (
              <div className="text-center py-4">
                <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
                <p className="text-muted mt-2 small">Loading packs…</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {filteredPacks.map(pack => {
                  const s = PACK_STYLES[pack.id] || PACK_STYLES.story;
                  return (
                    <div key={pack.id}
                      className="d-flex align-items-center gap-3 p-3 rounded-3"
                      style={{ background: s.bg, border: `1.5px solid ${s.border}` }}>
                      <div style={{ fontSize: '32px', flexShrink: 0, lineHeight: 1 }}>{pack.emoji}</div>
                      <div className="flex-grow-1">
                        <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{pack.label}</div>
                        <div style={{ color: 'var(--text-muted2)', fontSize: '13px' }}>{pack.description}</div>
                      </div>
                      <button
                        className="btn fw-bold rounded-pill px-3"
                        style={{
                          background: s.accent, color: 'white', border: 'none',
                          fontSize: '14px', flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                        onClick={() => handleBuy(pack.id)}
                        disabled={!!buying}
                      >
                        {buying === pack.id
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : packPrice(pack)}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-3 d-flex align-items-center gap-2"
              style={{ background: 'var(--purple-section)', borderRadius: '10px', padding: '10px 14px' }}>
              <i className="bi bi-shield-lock-fill" style={{ color: '#6f42c1', fontSize: '14px' }}></i>
              <span style={{ color: '#6f42c1', fontSize: '12px' }}>
                One-time payment {paymentStoreName() ? `through ${paymentStoreName()}` : 'via Stripe'}. Credits added instantly after checkout.
              </span>
            </div>

            {planName === 'free' && (
              <div className="mt-2 text-center">
                <span style={{ color: 'var(--text-soft)', fontSize: '12px' }}>
                  Or{' '}
                  <Link to="/pricing" style={{ color: '#6f42c1', fontWeight: 600 }}>upgrade your plan</Link>
                  {' '}for a much higher monthly limit.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopUpModal;
